/** Utilitários testáveis — dupla visualização lista (banco) + polling (Redis). */

export const POLL_MS_IMPORTACAO = 4000;

export const STATUS_IMPORTACAO_FINAIS = new Set([
  "concluido",
  "erro",
  "cancelado",
]);

export const STATUS_IMPORTACAO_ATIVOS = new Set(["pendente", "em_execucao"]);

export const CAMPOS_COLUNA_IMPORTACAO = [
  "cnpj",
  "razao_social",
  "codigo_banco",
  "agencia",
  "conta",
  "dt_inicio",
  "dt_fim",
  "saldo_conciliado",
  "situacao",
];

export const LABEL_STATUS_IMPORTACAO = {
  pendente: "Aguardando",
  em_execucao: "Em execução",
  concluido: "Concluído",
  erro: "Erro",
  cancelado: "Cancelado",
};

export const ETAPAS_LABEL_IMPORTACAO = {
  enfileirado: "Arquivo enfileirado",
  aguardando_fila: "Aguardando vez na fila",
  leitura_arquivo: "Lendo arquivo",
  extracao_texto: "Extraindo texto do PDF",
  identificacao_layout: "Identificando layout do banco",
  extracao_transacoes: "Lendo transações",
  extracao: "Extraindo dados",
  parse_ofx: "Interpretando OFX",
  validacao: "Validando dados",
  validacao_conta: "Validando conta",
  validacao_ofx: "Validando dados do OFX",
  validacao_extrato: "Validando período e duplicidade",
  persistencia: "Gravando no sistema",
  reagendado: "Nova tentativa",
  concluido: "Concluído",
  erro: "Erro na importação",
};

const normalizeDigits = (s) => String(s ?? "").replace(/\D/g, "");

export function formatarCnpjImportacao(cnpj) {
  const digits = normalizeDigits(cnpj);
  if (digits.length !== 14) return cnpj ? String(cnpj) : "—";
  return digits.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5"
  );
}

function formatarDataCurta(valor) {
  if (!valor) return "";
  const s = String(valor).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (y && m && d) return `${d}/${m}/${y}`;
  const dt = new Date(valor);
  if (Number.isNaN(dt.getTime())) return String(valor);
  return dt.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

export function formatarPeriodoImportacao(row) {
  if (!row || typeof row !== "object") return "—";
  const ini = formatarDataCurta(row.dt_inicio);
  const fim = formatarDataCurta(row.dt_fim);
  if (ini && fim) return ini === fim ? ini : `${ini} – ${fim}`;
  if (ini) return ini;
  if (fim) return fim;
  return "—";
}

export function formatarCodigoBancoImportacao(codigo) {
  const digits = normalizeDigits(codigo);
  if (!digits) return "—";
  return digits.padStart(3, "0");
}

/** Extrato/fila usam ``agencia|conta`` em `conta_bancaria`. */
export function parseAgenciaContaImportacao(contaBancaria) {
  const bruto = String(contaBancaria ?? "").trim();
  if (!bruto) return { agencia: "", conta: "" };
  if (bruto.includes("|")) {
    const [ag, ...rest] = bruto.split("|");
    return { agencia: (ag || "").trim(), conta: rest.join("|").trim() };
  }
  return { agencia: "", conta: bruto };
}

export function resolverAgenciaImportacao(row) {
  if (!row || typeof row !== "object") return "";
  if (row.agencia != null && String(row.agencia).trim()) {
    return String(row.agencia).trim();
  }
  return parseAgenciaContaImportacao(row.conta_bancaria).agencia;
}

export function resolverContaImportacao(row) {
  if (!row || typeof row !== "object") return "";
  if (row.conta != null && String(row.conta).trim()) {
    return String(row.conta).trim();
  }
  return parseAgenciaContaImportacao(row.conta_bancaria).conta;
}

export function formatarSaldoMonetarioImportacao(valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  const n = Number(valor);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function resolverSaldoFinalImportacao(row) {
  if (!row || typeof row !== "object") return null;
  if (row.saldo_final != null) return Number(row.saldo_final);
  if (row.saldo_atual != null) return Number(row.saldo_atual);
  return null;
}

export function resolverSaldoConciliadoImportacao(row) {
  if (!row || typeof row !== "object") return null;
  if (row.saldo_conciliado != null) return Number(row.saldo_conciliado);
  return null;
}

export function normalizarLinhas(lista) {
  return (lista || []).filter((row) => row && typeof row === "object");
}

export function aplicarFiltroImportacoes(lista, termo) {
  const t = String(termo ?? "").trim().toLowerCase();
  if (!t) return lista;
  return lista.filter(
    (row) =>
      row &&
      Object.values(row).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(t)
      )
  );
}

/**
 * Mescla jobs ativos do polling sobre a lista base (bootstrap do banco).
 * Detecta jobs que saíram do polling para reconsulta no banco (ex.: concluído).
 */
export function mesclarImportacoesPolling(
  listaBase,
  ativosPolling,
  idsAtivosAnteriores = new Set()
) {
  const ativos = normalizarLinhas(ativosPolling);
  const idsAtivosAgora = new Set(
    ativos.map((a) => a.id).filter((id) => id != null)
  );

  const map = new Map(
    normalizarLinhas(listaBase).map((r) => [r.id, { ...r }])
  );

  ativos.forEach((a) => {
    if (a?.id == null) return;
    map.set(a.id, { ...(map.get(a.id) || {}), ...a });
  });

  const idsPrecisamRefresh = [...idsAtivosAnteriores].filter(
    (id) => !idsAtivosAgora.has(id)
  );

  // RN-06 (Opção A): job ativo na lista base que sumiu do polling precisa reconsultar o banco
  for (const row of normalizarLinhas(listaBase)) {
    if (
      row?.id != null &&
      STATUS_IMPORTACAO_ATIVOS.has(row.status) &&
      !idsAtivosAgora.has(row.id) &&
      !idsPrecisamRefresh.includes(row.id)
    ) {
      idsPrecisamRefresh.push(row.id);
    }
  }

  const merged = [...map.values()].sort(
    (a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0)
  );

  return { merged, idsAtivosAgora, idsPrecisamRefresh };
}

export function formatarPercentualImportacao(valor) {
  if (valor == null || valor === "") return "—";
  const n = Number(valor);
  if (Number.isNaN(n)) return String(valor);
  return `${n}%`;
}

export function resolverEtapaLabelImportacao(row) {
  if (!row || typeof row !== "object") return "Processando…";
  if (row.etapa_label) return String(row.etapa_label);
  const codigo = row.etapa_codigo || row.etapa;
  if (codigo && ETAPAS_LABEL_IMPORTACAO[codigo]) {
    return ETAPAS_LABEL_IMPORTACAO[codigo];
  }
  return "Processando…";
}

export function resolverTooltipSituacaoImportacao(row) {
  if (!row || typeof row !== "object") return "";
  if (
    (row.status === "erro" || row.status === "cancelado") &&
    row.mensagem_erro
  ) {
    return String(row.mensagem_erro);
  }
  return String(row.detalhe || row.mensagem || "").trim();
}

/**
 * Resolve o conteúdo da coluna Situação (chip ou progresso).
 * `em_execucao` → barra de progresso; demais status → chip.
 */
export function resolverCelulaSituacaoImportacao(row) {
  if (!row || typeof row !== "object") {
    return { modo: "chip", status: "", label: "—", tooltip: "" };
  }

  const status = row.status || "";
  if (status === "em_execucao") {
    const raw = row.percentual;
    const n = raw != null && raw !== "" ? Number(raw) : null;
    const percentual = Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
    return {
      modo: "progresso",
      percentual,
      etapa: resolverEtapaLabelImportacao(row),
      tooltip: resolverTooltipSituacaoImportacao(row),
    };
  }

  return {
    modo: "chip",
    status,
    label: LABEL_STATUS_IMPORTACAO[status] || status || "—",
    tooltip: resolverTooltipSituacaoImportacao(row),
    chipColor:
      status === "pendente"
        ? "default"
        : status === "concluido"
          ? "success"
          : status === "erro"
            ? "error"
            : status === "cancelado"
              ? "default"
              : "default",
  };
}
